import { LABEL_CONSTANTS } from '../constants/common';
import { doesStripeAccountExist, getStripeAccountStatus } from '../api/stripeConnectApi';
import { StripeDocumentType, StripeMarketplaceAccess, StripeOnboardingStatus, StripePayoutBadgeVariant } from '../types/enums';
import {
  StripeAccountStatus,
  StripePayoutBadgeInfo,
  StripePayoutBannerInfo,
} from '../types/interfaces/stripeConnect.interfaces';

const TRUE_FLAG = 'true';
const IDENTITY_DOCUMENT_FIELD = 'individual.verification.document';
const IDENTITY_DOCUMENT_V2_FIELD = 'individual.documents.primary_verification';
const IDENTITY_DOCUMENT_PURPOSE = 'identity_document';
const ADDRESS_DOCUMENT_V2_FIELD = 'individual.documents.secondary_verification';
const ADDRESS_DOCUMENT_FIELD = 'individual.verification.additional_document';
const BANK_DOCUMENT_FIELD = 'bank_account_ownership_verification';

const isFlagTrue = (value: unknown): boolean => value === true || value === TRUE_FLAG;

const matchesOnboardingStatus = (
  status: StripeAccountStatus | null | undefined,
  expected: StripeOnboardingStatus,
): boolean => status?.onboardingStatus === expected;

const isIdentityDocumentField = (field: string): boolean => {
  const lower = field.toLowerCase();
  return (
    lower.includes(IDENTITY_DOCUMENT_V2_FIELD) ||
    lower.includes(IDENTITY_DOCUMENT_FIELD) ||
    lower.includes(IDENTITY_DOCUMENT_PURPOSE)
  );
};

const isAddressDocumentField = (field: string): boolean => {
  const lower = field.toLowerCase();
  return lower.includes(ADDRESS_DOCUMENT_V2_FIELD) || lower.includes(ADDRESS_DOCUMENT_FIELD);
};

const isBankDocumentField = (field: string): boolean => field.toLowerCase().includes(BANK_DOCUMENT_FIELD);

const isSupportedDocumentField = (field: string): boolean =>
  isIdentityDocumentField(field) || isBankDocumentField(field) || isAddressDocumentField(field);

const toFieldList = (value?: string[] | string | null): string[] => {
  if (value == null) return [];
  const items = Array.isArray(value) ? value : [value];
  return items.map(item => String(item).trim()).filter(Boolean);
};

const getOutstandingFields = (status?: StripeAccountStatus | null): string[] =>
  Array.from(new Set([...toFieldList(status?.currentlyDue), ...toFieldList(status?.pastDue)]));

export const normalizeVerificationReason = (field: string): string => {
  const trimmed = field.trim();
  if (isBankDocumentField(trimmed)) return BANK_DOCUMENT_FIELD;
  if (isIdentityDocumentField(trimmed)) return IDENTITY_DOCUMENT_FIELD;
  if (isAddressDocumentField(trimmed)) return ADDRESS_DOCUMENT_FIELD;
  return trimmed;
};

export const formatStripeReasonText = (text: string): string => {
  return text.replace(/[._]/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
};

export const formatStripeDeadline = (deadline?: string | null): string | null => {
  if (!deadline) return null;
  const date = new Date(deadline);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

export const toReasonItems = (value?: string[] | string | null): string[] => {
  return toFieldList(value).map(normalizeVerificationReason);
};

export const formatReasonItems = (value?: string[] | string | null): string | null => {
  const items = Array.from(new Set(toReasonItems(value)));
  if (items.length === 0) return null;
  return items.map(formatStripeReasonText).join(', ');
};

export const stripeStatusIsRestricted = (status?: StripeAccountStatus | null): boolean => {
  if (!status) return false;
  return matchesOnboardingStatus(status, StripeOnboardingStatus.Restricted);
};

export const stripeStatusIsEnabled = (status?: StripeAccountStatus | null): boolean => {
  return isFlagTrue(status?.payoutsEnabled) || matchesOnboardingStatus(status, StripeOnboardingStatus.Enabled);
};

export const getRequiredDocumentTypes = (status?: StripeAccountStatus | null): StripeDocumentType[] => {
  const outstandingFields = getOutstandingFields(status);
  const types: StripeDocumentType[] = [];
  if (outstandingFields.some(isIdentityDocumentField)) types.push(StripeDocumentType.Identity);
  if (outstandingFields.some(isBankDocumentField)) types.push(StripeDocumentType.BankAccount);
  if (outstandingFields.some(isAddressDocumentField)) types.push(StripeDocumentType.Address);
  return types;
};

export const stripeStatusRequiresDocument = (status?: StripeAccountStatus | null): boolean => {
  return getRequiredDocumentTypes(status).length > 0;
};

export const getStripeDocumentUploadTitle = (documentType?: StripeDocumentType | null): string => {
  switch (documentType) {
    case StripeDocumentType.Identity:
      return LABEL_CONSTANTS.STRIPE_UPLOAD_IDENTITY_DOCUMENT;
    case StripeDocumentType.BankAccount:
      return LABEL_CONSTANTS.STRIPE_UPLOAD_BANK_DOCUMENT;
    case StripeDocumentType.Address:
      return LABEL_CONSTANTS.STRIPE_UPLOAD_ADDRESS_DOCUMENT;
    default:
      return LABEL_CONSTANTS.DOCUMENT_UPLOAD;
  }
};

export const getStripeDocumentUploadHelpText = (
  documentType?: StripeDocumentType | null,
  status?: StripeAccountStatus | null,
): string => {
  if (status && !documentType) return LABEL_CONSTANTS.STRIPE_NO_DOCUMENT_REQUESTED;

  switch (documentType) {
    case StripeDocumentType.Identity:
      return LABEL_CONSTANTS.STRIPE_DOCUMENT_UPLOAD_HELP_IDENTITY;
    case StripeDocumentType.BankAccount:
      return LABEL_CONSTANTS.STRIPE_DOCUMENT_UPLOAD_HELP_BANK;
    case StripeDocumentType.Address:
      return LABEL_CONSTANTS.STRIPE_DOCUMENT_UPLOAD_HELP_ADDRESS;
    default:
      return LABEL_CONSTANTS.STRIPE_DOCUMENT_UPLOAD_HELP;
  }
};

export const stripeStatusIsPendingVerification = (status?: StripeAccountStatus | null): boolean => {
  if (!status) return false;
  if (stripeStatusIsRestricted(status) || stripeStatusRequiresDocument(status)) {
    return false;
  }
  return (
    isFlagTrue(status.isUnderReview) ||
    matchesOnboardingStatus(status, StripeOnboardingStatus.Submitted) ||
    toFieldList(status.pendingVerification).length > 0
  );
};

export const stripeStatusGetBadge = (
  status?: StripeAccountStatus | null,
  onboarded?: boolean,
): StripePayoutBadgeInfo | null => {
  if (!status) return null;

  if (isFlagTrue(status.requiresKycSubmission)) {
    return null;
  }

  if (stripeStatusIsRestricted(status)) {
    return {
      variant: StripePayoutBadgeVariant.Restricted,
      label: LABEL_CONSTANTS.ACCOUNT_RESTRICTED,
    };
  }

  if (stripeStatusRequiresDocument(status)) {
    return {
      variant: StripePayoutBadgeVariant.DocumentDue,
      label: LABEL_CONSTANTS.ACTION_REQUIRED_DOCUMENT_DUE,
    };
  }

  if (isFlagTrue(status.isActionRequiredSoon)) {
    return {
      variant: StripePayoutBadgeVariant.ActionRequiredSoon,
      label: LABEL_CONSTANTS.ACTION_REQUIRED_SOON,
    };
  }

  if (stripeStatusIsPendingVerification(status)) {
    return {
      variant: StripePayoutBadgeVariant.Pending,
      label: LABEL_CONSTANTS.PENDING_VERIFICATION,
    };
  }

  if (isFlagTrue(status.payoutsEnabled) || onboarded) {
    return {
      variant: StripePayoutBadgeVariant.Verified,
      label: LABEL_CONSTANTS.VERIFIED,
    };
  }

  return null;
};

export const stripeStatusGetInfoBanner = (
  badgeVariant?: StripePayoutBadgeVariant | null,
  status?: StripeAccountStatus | null,
): StripePayoutBannerInfo | null => {
  if (!badgeVariant) return null;

  switch (badgeVariant) {
    case StripePayoutBadgeVariant.Restricted: {
      const outstandingFields = getOutstandingFields(status);
      const restrictedContent = formatReasonItems(
        outstandingFields.length > 0 ? outstandingFields : status?.pendingVerification,
      );
      return {
        variant: StripePayoutBadgeVariant.Restricted,
        message: restrictedContent
          ? LABEL_CONSTANTS.STRIPE_RESTRICTED_DUE_TO_BANNER.replace('{details}', restrictedContent)
          : LABEL_CONSTANTS.STRIPE_RESTRICTED_BANNER_MESSAGE,
      };
    }
    case StripePayoutBadgeVariant.ActionRequiredSoon: {
      const dueItems = formatReasonItems(getOutstandingFields(status)) ?? '';
      const deadline = formatStripeDeadline(status?.currentDeadline);
      const message = deadline
        ? LABEL_CONSTANTS.STRIPE_ACTION_REQUIRED_SOON_BANNER.replace('{details}', dueItems).replace('{deadline}', deadline)
        : LABEL_CONSTANTS.STRIPE_ACTION_REQUIRED_SOON_NO_DEADLINE_BANNER.replace('{details}', dueItems);
      return {
        variant: StripePayoutBadgeVariant.ActionRequiredSoon,
        message: `${message} ${LABEL_CONSTANTS.STRIPE_ACTION_REQUIRED_SOON_UPDATE_DETAILS}`,
      };
    }
    case StripePayoutBadgeVariant.Pending: {
      const pendingContent = formatReasonItems(status?.pendingVerification);
      return {
        variant: StripePayoutBadgeVariant.Pending,
        message: pendingContent
          ? LABEL_CONSTANTS.STRIPE_PENDING_DUE_TO_BANNER.replace('{details}', pendingContent)
          : LABEL_CONSTANTS.STRIPE_PENDING_BANNER_MESSAGE,
      };
    }
    case StripePayoutBadgeVariant.DocumentDue: {
      const dueItems = formatReasonItems(getOutstandingFields(status).filter(isSupportedDocumentField));
      return {
        variant: StripePayoutBadgeVariant.DocumentDue,
        message: dueItems
          ? LABEL_CONSTANTS.STRIPE_DOCUMENT_DUE_TO_BANNER.replace('{details}', dueItems)
          : LABEL_CONSTANTS.STRIPE_DOCUMENT_REQUIRED_BANNER_MESSAGE,
      };
    }
    default:
      return null;
  }
};

export const getStripeMarketplaceAccess = async (accountId: number): Promise<StripeMarketplaceAccess> => {
  const { data: exists } = await doesStripeAccountExist(accountId);
  if (exists !== true) return StripeMarketplaceAccess.Setup;
  const { data: status } = await getStripeAccountStatus(accountId);

  if (isFlagTrue(status?.payoutsEnabled) && !stripeStatusIsRestricted(status)) {
    return StripeMarketplaceAccess.Enabled;
  }
  if (isFlagTrue(status?.requiresKycSubmission)) return StripeMarketplaceAccess.Setup;
  if (stripeStatusRequiresDocument(status)) return StripeMarketplaceAccess.Document;
  if (stripeStatusIsRestricted(status)) return StripeMarketplaceAccess.Restricted;
  if (stripeStatusIsPendingVerification(status)) return StripeMarketplaceAccess.Pending;

  return StripeMarketplaceAccess.Setup;
};

export const isStripePayoutMethodAvailable = async (accountId: number): Promise<boolean> => {
  try {
    const access = await getStripeMarketplaceAccess(accountId);
    return access === StripeMarketplaceAccess.Enabled;
  } catch {
    return false;
  }
};
